import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  ListItem,
  Chip,
  CircularProgress,
  alpha,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  useTheme,
  Container,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import HotelIcon from '@mui/icons-material/Hotel';
import NotesIcon from '@mui/icons-material/Notes';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import TagIcon from '@mui/icons-material/Tag';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LinkIcon from '@mui/icons-material/Link';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WebIcon from '@mui/icons-material/Web';
import ImageIcon from '@mui/icons-material/Image';
import NumbersIcon from '@mui/icons-material/Numbers';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import { format } from 'date-fns';
import { getActivityLogs, createTestLog } from '../../services/api';

// Map action types to human-readable descriptions
const actionDescriptions = {
  'HOTEL_UPDATED': 'updated a hotel',
  'HOTEL_CREATED': 'created a hotel',
  'HOTEL_DELETED': 'deleted a hotel',
  'USER_AUTHORIZED': 'authorized a user',
  'USER_REMOVED': 'removed a user',
  'CATEGORIES_UPDATED': 'updated categories',
  'TEST_LOG': 'created a test log'
};

// Map field names to icons
const fieldIcons = {
  // Hotel basic info
  'name': <HotelIcon fontSize="small" />,
  'notes': <NotesIcon fontSize="small" />,
  'phone': <PhoneIcon fontSize="small" />,
  'email': <EmailIcon fontSize="small" />,
  'address': <HomeIcon fontSize="small" />,
  'tags': <TagIcon fontSize="small" />,
  
  // Common category fields
  'chain': <BusinessIcon fontSize="small" />,
  'property': <HomeIcon fontSize="small" />,
  'propertynumber': <NumbersIcon fontSize="small" />,
  'website': <WebIcon fontSize="small" />,
  'location': <LocationOnIcon fontSize="small" />,
  'url': <LinkIcon fontSize="small" />,
  'coverimageurl': <ImageIcon fontSize="small" />,
  'active': <ToggleOnIcon fontSize="small" />,
  'date': <CalendarTodayIcon fontSize="small" />,
  'tag': <LocalOfferIcon fontSize="small" />,
  'status': <InfoIcon fontSize="small" />,
  
  // PMS and system fields
  'pms': <BusinessIcon fontSize="small" />,
  'pos': <BusinessIcon fontSize="small" />,
  'mice': <BusinessIcon fontSize="small" />,
  'spa': <BusinessIcon fontSize="small" />,
  'hr': <BusinessIcon fontSize="small" />,
  
  // Test data types
  'text': <NotesIcon fontSize="small" />,
  'boolean': <ToggleOnIcon fontSize="small" />,
  'number': <NumbersIcon fontSize="small" />,
  'array': <TagIcon fontSize="small" />,
  'object': <BusinessIcon fontSize="small" />,
  'null': <InfoIcon fontSize="small" />,
  'long-text': <NotesIcon fontSize="small" />,
};

// Format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown time';
  return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
};

// Helper function to render different value types
const renderValue = (value, fieldName = '') => {
  // Special handling for notes - extract just the content
  if (fieldName.toLowerCase() === 'notes' && Array.isArray(value)) {
    const noteContents = value.map(note => note.content).filter(Boolean);
    
    if (noteContents.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          height: '100%',
          p: 1,
          width: '100%'
        }}>
          <Typography component="em" sx={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.8rem' }}>
            No notes
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 1, width: '100%' }}>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2,
            pr: 0,
            py: 0.5,
            listStyleType: 'disc',
          }}
        >
          {noteContents.map((content, index) => (
            <Typography 
              component="li" 
              key={index}
              sx={{ 
                fontFamily: 'inherit', 
                fontSize: '0.85rem',
                mb: 0.5,
                lineHeight: 1.4
              }}
            >
              {content}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  }
  
  // Handle null/undefined
  if (value === undefined || value === null) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-start',
        height: '100%',
        p: 1,
        width: '100%'
      }}>
        <Typography 
          component="em" 
          sx={{ 
            opacity: 0.6, 
            fontStyle: 'italic',
            fontSize: '0.8rem' 
          }}
        >
          empty
        </Typography>
      </Box>
    );
  }
  
  // Handle booleans
  if (typeof value === 'boolean') {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-start',
        height: '100%',
        p: 1,
        width: '100%'
      }}>
        <Typography 
          sx={{ 
            fontFamily: 'monospace', 
            fontSize: '0.85rem',
            color: value ? 'success.main' : 'text.secondary',
            fontWeight: 500,
            px: 1
          }}
        >
          {value ? 'true' : 'false'}
        </Typography>
      </Box>
    );
  }
  
  // Check for date values or ISO strings
  if (
    (typeof value === 'string' && 
     (
       // Check for ISO date string format
       /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(value) ||
       // Field name contains date or time
       fieldName.toLowerCase().includes('date') || 
       fieldName.toLowerCase().includes('time')
     )
    ) || 
    (value instanceof Date)
  ) {
    try {
      const date = value instanceof Date ? value : new Date(value);
      // Check if it's a valid date
      if (!isNaN(date.getTime())) {
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            height: '100%',
            p: 1,
            width: '100%'
          }}>
            <Typography 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.85rem',
                color: 'info.main',
                px: 1
              }}
            >
              {format(date, 'MMM d, yyyy h:mm:ss a')}
            </Typography>
          </Box>
        );
      }
    } catch (e) {
      // If date parsing fails, continue with normal string handling
    }
  }
  
  // Handle objects and arrays
  if (typeof value === 'object') {
    try {
      // Check for special cases like functions, errors or DOM nodes
      if (typeof value.toString === 'function' && value.toString() !== '[object Object]' && value.toString().includes('function')) {
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            height: '100%',
            p: 1,
            width: '100%'
          }}>
            <Typography 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.85rem',
                color: 'text.secondary',
                fontStyle: 'italic',
                px: 1
              }}
            >
              [Function]
            </Typography>
          </Box>
        );
      }
      
      let formatted;
      // Handle Arrays with more than 5 items
      if (Array.isArray(value) && value.length > 5) {
        const sample = value.slice(0, 5);
        formatted = JSON.stringify(sample, null, 2) + `\n... ${value.length - 5} more items`;
      } else {
        // Limit stringification depth to prevent overly complex objects
        const safeStringify = (obj, depth = 0) => {
          if (depth > 2) return "[Complex Object]";
          
          if (Array.isArray(obj)) {
            return '[' + obj.map(item => {
              if (typeof item === 'object' && item !== null) {
                return safeStringify(item, depth + 1);
              }
              return JSON.stringify(item);
            }).join(',') + ']';
          }
          
          if (typeof obj === 'object' && obj !== null) {
            const pairs = [];
            for (const key in obj) {
              if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key];
                if (typeof value === 'object' && value !== null) {
                  pairs.push(`"${key}": ${safeStringify(value, depth + 1)}`);
                } else {
                  pairs.push(`"${key}": ${JSON.stringify(value)}`);
                }
              }
            }
            return '{' + pairs.join(',') + '}';
          }
          
          return JSON.stringify(obj);
        };
        
        formatted = safeStringify(value);
        
        // If still too long, truncate
        if (formatted && formatted.length > 500) {
          formatted = formatted.substring(0, 500) + '... [truncated]';
        }
      }
      
      // Check if object is very simple (one level, few properties)
      const isSimpleObject = formatted.length < 80 && !formatted.includes('\n');
      
      return (
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          p: 1
        }}>
          {isSimpleObject ? (
            <Typography 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.8rem',
                px: 1,
                whiteSpace: 'nowrap',
                overflowX: 'auto'
              }}
            >
              {formatted}
            </Typography>
          ) : (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                width: '100%',
                maxWidth: '100%',
                maxHeight: '150px',
                overflowY: 'auto',
                bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                borderRadius: 1
              }}
            >
              {formatted}
            </Box>
          )}
        </Box>
      );
    } catch (e) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          height: '100%',
          p: 1,
          width: '100%'
        }}>
          <Typography 
            sx={{ 
              fontFamily: 'monospace', 
              fontSize: '0.85rem',
              fontStyle: 'italic',
              px: 1,
              opacity: 0.7
            }}
          >
            [Complex Object]
          </Typography>
        </Box>
      );
    }
  }
  
  // Handle strings
  const stringValue = String(value);
  const isLongString = stringValue.length > 100;
  const hasMultipleLines = stringValue.includes('\n');
  
  if (isLongString || hasMultipleLines) {
    return (
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        p: 1
      }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            width: '100%',
            maxWidth: '100%',
            maxHeight: '150px',
            overflowY: 'auto',
            bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
            borderRadius: 1
          }}
        >
          {stringValue}
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'flex-start',
      height: '100%',
      p: 1,
      width: '100%'
    }}>
      <Typography 
        sx={{ 
          fontFamily: 'monospace', 
          fontSize: '0.85rem',
          px: 1,
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          width: '100%'
        }}
      >
        {stringValue}
      </Typography>
    </Box>
  );
};

const ActivityLogs = () => {
  const theme = useTheme();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [paginatedLogs, setPaginatedLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');
  
  // Activity detection
  const activeRef = useRef(true);
  const lastActivityRef = useRef(Date.now());
  const activityCheckIntervalRef = useRef(null);
  const INACTIVE_THRESHOLD = 10 * 60 * 1000; // 10 minutes
  
  // Track user activity
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    activeRef.current = true;
  }, []);
  
  // Check if user has been inactive
  const checkActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    if (timeSinceLastActivity > INACTIVE_THRESHOLD) {
      console.log('User inactive for more than 10 minutes, pausing log updates');
      activeRef.current = false;
    } else {
      activeRef.current = true;
    }
  }, [INACTIVE_THRESHOLD]);
  
  // Set up activity tracking
  useEffect(() => {
    // Track various user interactions
    const events = ['mousedown', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, trackActivity));
    
    // Check activity status every minute
    activityCheckIntervalRef.current = setInterval(checkActivity, 60 * 1000);
    
    return () => {
      events.forEach(event => window.removeEventListener(event, trackActivity));
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
      }
    };
  }, [trackActivity, checkActivity]);

  const fetchLogs = useCallback(async (forceRefresh = false) => {
    // Don't fetch if the user is inactive unless forced
    if (!activeRef.current && !forceRefresh) {
      console.log('Skipping log fetch due to user inactivity');
      return;
    }
    
    try {
      setLoading(true);
      const logsData = await getActivityLogs(100, forceRefresh);
      setLogs(logsData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
      setError('Failed to load activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch logs on initial mount
    fetchLogs();
    
    // Set up periodic refresh when component is active
    const refreshInterval = setInterval(() => {
      if (activeRef.current && document.visibilityState === 'visible') {
        fetchLogs(false);
      }
    }, 5 * 60 * 1000); // Refresh every 5 minutes if active
    
    // Check for visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeRef.current) {
        // Refresh when tab becomes visible and user is active
        fetchLogs(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchLogs]);

  const handleRefresh = () => {
    trackActivity(); // Mark user as active
    fetchLogs(true); // Force refresh
  };

  const handleCreateTestLog = async () => {
    try {
      trackActivity(); // Mark user as active
      setLoading(true);
      await createTestLog();
      
      // Show visual feedback (temporary success state)
      const testButton = document.querySelector('[aria-label="Create a test log with various data types to verify display functionality"]');
      if (testButton) {
        // Save original styles
        const originalBackground = testButton.style.backgroundColor;
        const originalTransform = testButton.style.transform;
        
        // Apply success animation
        testButton.style.backgroundColor = alpha(theme.palette.success.main, 0.3);
        testButton.style.transform = 'scale(1.1)';
        
        // Restore original styles after animation
        setTimeout(() => {
          testButton.style.backgroundColor = originalBackground;
          testButton.style.transform = originalTransform;
        }, 500);
      }
      
      await fetchLogs(true); // Force refresh after creating a test log
    } catch (err) {
      console.error('Failed to create test log:', err);
      setError('Failed to create test log. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter logs based on search term and action filter
  useEffect(() => {
    // Memoization optimization - avoid unnecessary re-filtering
    const filterLogs = () => {
      // First filter out logs that have no changes (unless they're special log types)
      const logsWithChanges = logs.filter(log => {
        // Always keep special action types (creation, deletion, test logs, etc.)
        if (
          log.action === 'HOTEL_CREATED' || 
          log.action === 'HOTEL_DELETED' || 
          log.action === 'USER_AUTHORIZED' ||
          log.action === 'USER_REMOVED' ||
          log.action === 'TEST_LOG'
        ) {
          return true;
        }
        
        // For update actions, only keep if there are actual changes
        if (log.action === 'HOTEL_UPDATED' || log.action === 'CATEGORIES_UPDATED') {
          // Check if we have changes data
          if (log.details?.changes && Array.isArray(log.details.changes) && log.details.changes.length > 0) {
            return true;
          }
          
          // Check older format with changedFields
          if (log.details?.changedFields && Array.isArray(log.details.changedFields) && log.details.changedFields.length > 0) {
            return true;
          }
          
          // No changes found, filter out this log
          return false;
        }
        
        // Keep other log types by default
        return true;
      });
      
      // Then apply action filter
      let actionFiltered = logsWithChanges;
      if (actionFilter !== 'all') {
        actionFiltered = logsWithChanges.filter(log => log.action === actionFilter);
      }
      
      // Finally apply search term filter
      const searchFiltered = actionFiltered.filter(log => {
        if (!searchTerm) return true;
        
        const searchTermLower = searchTerm.toLowerCase();
        return (
          (log.user?.email && log.user.email.toLowerCase().includes(searchTermLower)) ||
          (log.details?.hotelName && log.details.hotelName.toLowerCase().includes(searchTermLower)) ||
          (log.action && actionDescriptions[log.action]?.toLowerCase().includes(searchTermLower)) ||
          (log.details?.changedFields && log.details.changedFields.some(field => 
            field.toLowerCase().includes(searchTermLower)
          )) ||
          (log.details?.changes && log.details.changes.some(change => 
            change.field.toLowerCase().includes(searchTermLower) ||
            (typeof change.from === 'string' && change.from.toLowerCase().includes(searchTermLower)) ||
            (typeof change.to === 'string' && change.to.toLowerCase().includes(searchTermLower))
          ))
        );
      });
      
      return searchFiltered;
    };
    
    const filtered = filterLogs();
    setFilteredLogs(filtered);
    
    // Calculate total pages
    setTotalPages(Math.max(1, Math.ceil(filtered.length / rowsPerPage)));
    
    // Reset to page 1 if we changed filters and current page would be out of bounds
    if (page > Math.ceil(filtered.length / rowsPerPage)) {
      setPage(1);
    }
  }, [logs, searchTerm, actionFilter, rowsPerPage, page]);
  
  // Paginate the filtered logs
  useEffect(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setPaginatedLogs(filteredLogs.slice(startIndex, endIndex));
  }, [filteredLogs, page, rowsPerPage]);
  
  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };
  
  // Helper to get action color
  const getActionColor = (action) => {
    switch(action) {
      case 'HOTEL_UPDATED': return theme.palette.primary.main;
      case 'HOTEL_CREATED': return theme.palette.success.main;
      case 'TEST_LOG': return theme.palette.info.main;
      case 'USER_AUTHORIZED': return theme.palette.warning.main;
      case 'USER_REMOVED': 
      case 'HOTEL_DELETED': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };
  
  return (
    <Container 
      maxWidth={false} 
      disableGutters 
      sx={{ 
        maxWidth: 1200, 
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative'
      }}
    >
      {/* Header area */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3, 
          borderRadius: 3,
          background: theme => `linear-gradient(145deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.background.paper, 1)} 100%
          )`,
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0 // Prevent header from shrinking
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 2
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              fontFamily: 'StrawberrySansDisplay',
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
          >
            Activity Logs
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              placeholder="Search logs..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', opacity: 0.7 }} />
                  </InputAdornment>
                )
              }}
              sx={{ 
                flex: 1,
                minWidth: { xs: 0, sm: 240 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme => alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: theme => alpha(theme.palette.background.paper, 1)
                  }
                }
              }}
            />
            
            <Tooltip title="Create a test log with various data types to verify display functionality">
              <IconButton
                onClick={handleCreateTestLog}
                sx={{
                  height: 40, 
                  width: 40,
                  bgcolor: theme => alpha(theme.palette.success.main, 0.08),
                  color: 'success.main',
                  border: '1px dashed',
                  borderColor: 'success.main',
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.success.main, 0.12),
                    boxShadow: '0 0 0 4px ' + alpha(theme.palette.success.main, 0.1)
                  }
                }}
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Refresh logs">
              <span>
                <IconButton 
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    height: 40, 
                    width: 40,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                    }
                  }}
                >
                  <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="action-filter-label">Filter by Action</InputLabel>
            <Select
              labelId="action-filter-label"
              value={actionFilter}
              label="Filter by Action"
              onChange={(e) => setActionFilter(e.target.value)}
              sx={{
                borderRadius: 2,
                backgroundColor: theme => alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(8px)'
              }}
            >
              <MenuItem value="all">All Actions</MenuItem>
              <MenuItem value="HOTEL_UPDATED">Hotel Updated</MenuItem>
              <MenuItem value="HOTEL_CREATED">Hotel Created</MenuItem>
              <MenuItem value="HOTEL_DELETED">Hotel Deleted</MenuItem>
              <MenuItem value="USER_AUTHORIZED">User Authorized</MenuItem>
              <MenuItem value="USER_REMOVED">User Removed</MenuItem>
              <MenuItem value="CATEGORIES_UPDATED">Categories Updated</MenuItem>
              <MenuItem value="TEST_LOG">Test Logs</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="rows-per-page-label">Logs per page</InputLabel>
            <Select
              labelId="rows-per-page-label"
              value={rowsPerPage}
              label="Logs per page"
              onChange={handleRowsPerPageChange}
              sx={{
                borderRadius: 2,
                backgroundColor: theme => alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(8px)'
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      {/* Content area */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 0, sm: 0 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, height: '100%' }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="body1">{error}</Typography>
          </Box>
        ) : paginatedLogs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {logs.length === 0 ? 'No activity logs found.' : 'No logs match your search criteria.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {/* Activity Timeline continues as before... */}
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
              <Box 
                className="activity-timeline" 
                sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: { xs: 15, sm: 20 },
                    width: 2,
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                    zIndex: 0
                  }
                }}
              >
                {paginatedLogs.map((log, index) => (
                  <React.Fragment key={log.id || index}>
                    <ListItem
                      disableGutters
                      disablePadding
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        p: 0,
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ 
                        pl: { xs: 6, sm: 7 },
                        pr: { xs: 2, sm: 3 },
                        py: 2.5,
                        display: 'flex', 
                        flexDirection: 'column',
                        width: '100%',
                        position: 'relative', 
                        zIndex: 1,
                        '&:hover': {
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.03)
                        }
                      }}>
                        {/* Timeline marker */}
                        <Box sx={{
                          position: 'absolute',
                          left: { xs: 20, sm: 24 },
                          top: 22,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getActionColor(log.action),
                          border: '2px solid',
                          borderColor: 'background.paper',
                          zIndex: 2
                        }} />
                        
                        {/* User avatar/icon */}
                        <Box sx={{
                          position: 'absolute',
                          left: { xs: 14, sm: 18 },
                          top: 50,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2,
                          fontSize: '0.8rem'
                        }}>
                          <PersonIcon fontSize="small" />
                        </Box>
                        
                        {/* Header with user info & timestamp */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          mb: 1,
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 0.5, sm: 0 },
                          flexWrap: 'wrap'
                        }}>
                          {/* User and action */}
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {log.user?.email || 'Unknown user'}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                              {actionDescriptions[log.action] || log.action}
                            </Typography>
                            
                            {/* User role */}
                            {log.user?.role && (
                              <Chip
                                size="small"
                                label={log.user.role.toUpperCase()}
                                sx={{ 
                                  ml: 1,
                                  borderRadius: 1,
                                  bgcolor: theme => {
                                    const roleColors = {
                                      admin: alpha(theme.palette.error.main, 0.1),
                                      editor: alpha(theme.palette.warning.main, 0.1),
                                      SOS: alpha(theme.palette.info.main, 0.1)
                                    };
                                    return roleColors[log.user.role] || alpha(theme.palette.grey[500], 0.1);
                                  },
                                  color: theme => {
                                    const roleColors = {
                                      admin: theme.palette.error.main,
                                      editor: theme.palette.warning.main,
                                      SOS: theme.palette.info.main
                                    };
                                    return roleColors[log.user.role] || theme.palette.grey[700];
                                  },
                                  fontWeight: 500,
                                  fontSize: '0.65rem',
                                  height: 18
                                }}
                              />
                            )}
                          </Box>
                          
                          {/* Timestamp */}
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </Box>
                        
                        {/* Content */}
                        <Paper
                          elevation={0}
                          sx={{ 
                            mt: 1,
                            ml: { xs: 0, sm: 0.5 },
                            p: 0, 
                            borderRadius: 2,
                            bgcolor: theme => alpha(theme.palette.background.default, 0.5),
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Hotel information */}
                          {log.entityType === 'hotel' && log.details?.hotelName && (
                            <Box 
                              sx={{ 
                                p: { xs: 1.5, sm: 2 },
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                                borderBottom: '1px solid',
                                borderColor: theme => alpha(theme.palette.primary.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap'
                              }}
                            >
                              <HotelIcon color="primary" sx={{ opacity: 0.7 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {log.details.hotelName}
                                {log.details.hotelPropertyNumber && (
                                  <Typography 
                                    component="span" 
                                    variant="caption" 
                                    sx={{ ml: 0.5, color: 'text.secondary' }}
                                  >
                                    ({log.details.hotelPropertyNumber})
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Changed fields */}
                          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                            {log.details?.changedFields && Array.isArray(log.details.changedFields) && log.details.changedFields.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ 
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '0.7rem',
                                    '&::after': {
                                      content: '""',
                                      display: 'block',
                                      height: 1,
                                      bgcolor: 'divider',
                                      ml: 1,
                                      flex: 1
                                    }
                                  }}
                                >
                                  Changes
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {/* If we have the new changes array with values, use that */}
                                  {log.details.changes && Array.isArray(log.details.changes) ? (
                                    <>
                                      {/* Show expanded details for all changes */}
                                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {log.details.changes.map((change) => (
                                          <Paper
                                            key={change.field}
                                            elevation={0}
                                            sx={{ 
                                              display: 'flex', 
                                              flexDirection: 'column',
                                              borderRadius: 2,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              overflow: 'hidden'
                                            }}
                                          >
                                            {/* Field header */}
                                            <Box sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              gap: 1, 
                                              p: 1.5,
                                              bgcolor: alpha(theme.palette.background.paper, 0.6),
                                              borderBottom: '1px solid',
                                              borderColor: 'divider'
                                            }}>
                                              <Box 
                                                sx={{ 
                                                  width: 24, 
                                                  height: 24, 
                                                  borderRadius: '50%',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                                  color: 'primary.main',
                                                  flexShrink: 0
                                                }}
                                              >
                                                {fieldIcons[change.field.toLowerCase()] || <TagIcon fontSize="small" />}
                                              </Box>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {change.field}
                                              </Typography>
                                            </Box>
                                            
                                            {/* Value comparison */}
                                            <Box sx={{
                                              display: 'flex', 
                                              flexDirection: { xs: 'column', sm: 'row' },
                                              width: '100%'
                                            }}>
                                              {/* Previous value */}
                                              <Box sx={{ 
                                                flex: '1 1 50%', 
                                                borderRight: { xs: 'none', sm: '1px solid' },
                                                borderBottom: { xs: '1px solid', sm: 'none' },
                                                borderColor: 'divider',
                                                display: 'flex',
                                                flexDirection: 'column'
                                              }}>
                                                <Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    color: 'error.main', 
                                                    display: 'block', 
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.02em',
                                                    px: 2,
                                                    pt: 1.5,
                                                    pb: 0.75,
                                                  }}
                                                >
                                                  Previous Value
                                                </Typography>
                                                <Box sx={{ 
                                                  flex: 1,
                                                  bgcolor: theme => alpha(theme.palette.error.main, 0.03),
                                                  border: '1px solid',
                                                  borderColor: theme => alpha(theme.palette.error.main, 0.15),
                                                  borderRadius: 1,
                                                  m: 1,
                                                  display: 'flex',
                                                  overflow: 'hidden'
                                                }}>
                                                  {renderValue(change.from, change.field)}
                                                </Box>
                                              </Box>
                                              
                                              {/* Remove arrow and replace with subtle divider */}
                                              {/* Mobile divider (visible only in xs) */}
                                              <Box 
                                                sx={{
                                                  display: { xs: 'flex', sm: 'none' },
                                                  width: '100%',
                                                  height: '1px',
                                                  bgcolor: theme => alpha(theme.palette.divider, 0.5),
                                                  my: 1
                                                }}
                                              />
                                              
                                              {/* New value */}
                                              <Box sx={{ 
                                                flex: '1 1 50%',
                                                display: 'flex',
                                                flexDirection: 'column'
                                              }}>
                                                <Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    color: 'success.main', 
                                                    display: 'block', 
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.02em',
                                                    px: 2,
                                                    pt: 1.5,
                                                    pb: 0.75,
                                                  }}
                                                >
                                                  New Value
                                                </Typography>
                                                <Box sx={{ 
                                                  flex: 1,
                                                  bgcolor: theme => alpha(theme.palette.success.main, 0.03),
                                                  border: '1px solid',
                                                  borderColor: theme => alpha(theme.palette.success.main, 0.15),
                                                  borderRadius: 1,
                                                  m: 1, 
                                                  display: 'flex',
                                                  overflow: 'hidden'
                                                }}>
                                                  {renderValue(change.to, change.field)}
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Paper>
                                        ))}
                                      </Box>
                                    </>
                                  ) : (
                                    // Fallback to old format if changes array not available
                                    log.details.changedFields.map((field) => (
                                      <Chip
                                        key={field}
                                        size="small"
                                        icon={fieldIcons[field.toLowerCase()] || null}
                                        label={field}
                                        sx={{ 
                                          borderRadius: 1,
                                          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                          color: 'primary.main',
                                          fontWeight: 500,
                                          fontSize: '0.75rem',
                                          height: 28
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ py: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {log.action === 'TEST_LOG' ? 
                                    "This is a test log entry" : 
                                    "No specific changes were recorded for this action"}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    </ListItem>
                    {index < paginatedLogs.length - 1 && (
                      <Divider sx={{ mx: { xs: 2, sm: 3 }, ml: { xs: 6, sm: 7 } }} />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Pagination controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange} 
            color="primary"
            disabled={loading}
            siblingCount={1}
            showFirstButton
            showLastButton
          />
        </Box>
      </Paper>
      
      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default ActivityLogs; 